import { Box, Typography, TextField, Button, InputAdornment, Divider, ToggleButton, ToggleButtonGroup, RadioGroup, FormControlLabel, Radio, Paper } from "@mui/material";
import AlignHorizontalLeftIcon from "@mui/icons-material/AlignHorizontalLeft";
import AlignHorizontalRightIcon from "@mui/icons-material/AlignHorizontalRight";
import Tooltip from "../components/ui/tooltip";
import React, { useState } from "react";

import Topbar from "../components/topbar";
import XeroSyncButton from "../components/xero-sync-button";
import { useToast } from "../providers/toaster-provider";
import { useUser } from "../providers/user-provider";
import { usePermissions } from "../providers/permissions-provider";
import { usePlanFeature } from "../hooks/use-plan-feature";
import PlanUpgradeBar from "../components/ui/plan-upgrade-bar";
import Dropdown, { DropdownOption } from "../components/ui/dropdown";
import Toggle from "../components/ui/toggle";

import useApi from '../hooks/use-api';
import buildApiRequestConfig from '../api/url-builder';
import routes from '../api/routes';
import SectionWrapper from "../components/section";
import StatusConfigTable from "../components/status-config-table";
import { useSettings } from "../providers/settings-provider";
import { InvoicePreviewMock, PreviewScaler } from "../components/invoice-preview-mock";

export const defaultInvoiceTerms = 'This invoice is valid for 7 days. To approve it please write your name and the words - OK to proceed - with your signature on this page and fax back to (enter your fax number)';

export const defaultQuoteTerms = 'Quote valid for 30 days. Local taxes excluded unless noted. Payment due by invoice date. Goods remain the property of the supplier until paid in full. Variations or additional work will be charged separately. After acceptance cancellations must be made in writing and shall incur fees. Quote will be considered accepted if the client partially pays or accepts delivery of goods stated above.';

const InvoiceSettings: React.FC = () => {
    const { user } = useUser();
    const { can } = usePermissions();
    const isReadOnly = !can('settings_invoice', 'update');
    // Statuses are Advanced features - read-only without it: any click in the
    // table opens the upgrade / add-on prompt instead of changing anything.
    const { has: hasAdvanced, prompt: promptAdvanced } = usePlanFeature('advancedFeatures');
    const blockStatusEdit = (e: React.SyntheticEvent) => {
        if (hasAdvanced) return;
        const target = e.target as HTMLElement;
        if (!target.closest('button, input, textarea, select, label, [role="button"], [role="checkbox"], [role="combobox"], [role="switch"]')) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'click') promptAdvanced();
    };
    // Adding / removing a status are their own Create / Delete permissions.
    const canCreateStatus = can('settings_invoice', 'create');
    const canDeleteStatus = can('settings_invoice', 'delete');
    const { showSuccess, showError } = useToast();
    const { companySettings } = useSettings();

    const [status, setStatus] = useState('');
    const [statusOptions, setStatusOptions] = useState<DropdownOption[]>([]);
    const [customizableStatusOptions, setCustomizableStatusOptions] = useState<DropdownOption[]>([]);
    const [invoiceHeader, setInvoiceHeader] = useState('');
    const [uploadedImage, setUploadedImage] = useState<string | null>(null);
    const [uploadedFileName, setUploadedFileName] = useState<string>('');
    const [file, setFile] = useState<File | null>(null);
    const [termsAndConditions, setTermsAndConditions] = useState(defaultInvoiceTerms);
    const [provisionDate, setProvisionDate] = useState('7');
    const [nextInvoiceNumber, setNextInvoiceNumber] = useState('0000001');
    const [fromAddress, setFromAddress] = useState('');
    const [fromPhone, setFromPhone] = useState('');
    const [fromFax, setFromFax] = useState('');
    const [fromEmail, setFromEmail] = useState('');
    const [titlePosition, setTitlePosition] = useState<'left' | 'right'>('left');
    const [hideHeaderImage, setHideHeaderImage] = useState(false);
    const [showTitle, setShowTitle] = useState(true);
    const [showContactInfo, setShowContactInfo] = useState(true);

    // Track changes for save button state
    const [hasChanges, setHasChanges] = React.useState(false);
    const [originalValues, setOriginalValues] = React.useState({
        termsAndConditions: '',
        provisionDate: '',
        nextInvoiceNumber: '',
        invoiceHeader: '',
        status: '',
        fromAddress: '',
        fromPhone: '',
        fromFax: '',
        fromEmail: '',
        titlePosition: 'left',
        hideHeaderImage: false,
        showTitle: true,
        showContactInfo: true,
    });

    const [fetchInvoiceSettings, setFetchInvoiceSettings] = useApi();
    const [fetchInvoiceStatuses, setFetchInvoiceStatuses] = useApi();
    const [saveStatus, setSaveStatus] = useApi();
    const [updateStatus, setUpdateStatus] = useApi();
    const [deleteStatus, setDeleteStatus] = useApi();
    const [statusConfigs, setStatusConfigs] = React.useState<any[]>([]);

    // Function to check if any changes have been made
    const checkForChanges = React.useCallback(() => {
        const hasAnyChanges =
            termsAndConditions !== originalValues.termsAndConditions ||
            provisionDate !== originalValues.provisionDate ||
            nextInvoiceNumber !== originalValues.nextInvoiceNumber ||
            invoiceHeader !== originalValues.invoiceHeader ||
            status !== originalValues.status ||
            fromAddress !== originalValues.fromAddress ||
            fromPhone !== originalValues.fromPhone ||
            fromFax !== originalValues.fromFax ||
            fromEmail !== originalValues.fromEmail ||
            titlePosition !== originalValues.titlePosition ||
            hideHeaderImage !== originalValues.hideHeaderImage ||
            showTitle !== originalValues.showTitle ||
            showContactInfo !== originalValues.showContactInfo ||
            file !== null;

        setHasChanges(hasAnyChanges);
    }, [termsAndConditions, provisionDate, nextInvoiceNumber, invoiceHeader, status, fromAddress, fromPhone, fromFax, fromEmail, titlePosition, hideHeaderImage, showTitle, showContactInfo, file, originalValues]);

    // Check for changes whenever relevant values change
    React.useEffect(() => {
        checkForChanges();
    }, [checkForChanges]);

    React.useEffect(() => {
        if (!user?.entityId || !status) return;

        const { url: fetchUrl, method: fetchMethod } = buildApiRequestConfig(routes.invoiceSettings.fetchByEntity, [
            { param: 'entityId', value: user.entityId },
            { param: 'statusId', value: status },
        ]);
        
        setFetchInvoiceSettings({ url: fetchUrl, method: fetchMethod });
    }, [user?.entityId, status, setFetchInvoiceSettings]);

    React.useEffect(() => {
        if (!user?.entityId) return;

        const { url: fetchStatus, method: statusMethod } = buildApiRequestConfig(routes.invoiceSettings.fetchStatusesByEntity, [
            { param: 'entityId', value: user.entityId }
        ]);

        setFetchInvoiceStatuses({ url: fetchStatus, method: statusMethod });
    }, [user?.entityId, saveStatus, updateStatus, deleteStatus, setFetchInvoiceStatuses]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetchInvoiceSettings;
        if (data && !isLoading ) {
            // Don't overwrite status if user just selected it - only update other fields
            setInvoiceHeader(data.headerFileName || '');
            setTermsAndConditions(data.termsAndConditions || (getStatusSystemName(status) === 'default_invoice' ? defaultInvoiceTerms : getStatusSystemName(status) === 'default_quote' ? defaultQuoteTerms : ''));
            setProvisionDate(data.provisionDateOffset?.toString() || '7');
            setNextInvoiceNumber(data.nextInvoiceNumber || '0000001');
            setUploadedImage(data.headerUrl || null);
            setFromAddress(data.fromAddress || '');
            setFromPhone(data.fromPhone || '');
            setFromFax(data.fromFax || '');
            setFromEmail(data.fromEmail || '');
            setTitlePosition(data.titlePosition === 'right' ? 'right' : 'left');
            setHideHeaderImage(data.hideHeaderImage === true);
            setShowTitle(data.showTitle !== false);
            setShowContactInfo(data.showContactInfo !== false);

            // Set original values for change tracking
            setOriginalValues({
                termsAndConditions: data.termsAndConditions || (getStatusSystemName(status) === 'default_invoice' ? defaultInvoiceTerms : getStatusSystemName(status) === 'default_quote' ? defaultQuoteTerms : ''),
                provisionDate: data.provisionDateOffset?.toString() || '7',
                nextInvoiceNumber: data.nextInvoiceNumber || '0000001',
                invoiceHeader: data.headerFileName || '',
                status: status || '',
                fromAddress: data.fromAddress || '',
                fromPhone: data.fromPhone || '',
                fromFax: data.fromFax || '',
                fromEmail: data.fromEmail || '',
                titlePosition: data.titlePosition === 'right' ? 'right' : 'left',
                hideHeaderImage: data.hideHeaderImage === true,
                showTitle: data.showTitle !== false,
                showContactInfo: data.showContactInfo !== false,
            });
            
            // Reset changes flag
            setHasChanges(false);
        }
        if (error) {
            showError('Failed to fetch invoice settings');
        }
    }, [fetchInvoiceSettings, showError, status]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetchInvoiceStatuses;
        if (data && !isLoading) {
            const options = data?.map((status: any) => ({ label: status.name, value: String(status.id) }));
            setStatusOptions(options);
            setCustomizableStatusOptions(data?.filter((status: any) => status.isCustomizable).map((status: any) => ({ label: status.name, value: String(status.id) })));
            // Convert to status config format for table
            const configs = data.map((s: any) => ({
                id: s.id,
                name: s.name,
                systemName: s.systemName ?? null,
                isDefault: s.isDefault ?? false,
                isLocked: s.isLocked ?? false,
                customizable: s.isCustomizable ?? true,
                nextStatusId: String(s.nextStatusId || ''),
                lockedItems: {
                    companyDetails: s.isCompanyEditable ?? false,
                    lineItems: s.isLineItemEditable ?? false,
                    status: s.isStatusEditable ?? false
                }
            }));
            setStatusConfigs(configs);
            setStatus(data.find((s: any) => s.isDefault)?.id || '');
        }
        if (error) {
            showError('Failed to fetch invoice statuses');
        }
    }, [fetchInvoiceStatuses, showError]);

    // Helper to get status name from ID
    const getStatusName = (statusId: string | number) => {
        const statusConfig = statusConfigs.find(s => String(s.id) === String(statusId));
        return statusConfig?.name || '';
    };

    // Helper to get the stable systemName from ID (unaffected by renaming, unlike name)
    const getStatusSystemName = (statusId: string | number) => {
        const statusConfig = statusConfigs.find(s => String(s.id) === String(statusId));
        return statusConfig?.systemName || '';
    };

    // Helper function to convert local status config to API payload
    const buildStatusUpdatePayload = (status: any) => {
        return {
            name: status.name,
            isDefault: status.isDefault,
            isActive: true,
            isCompanyEditable: status.lockedItems?.companyDetails ?? false,
            isLineItemEditable: status.lockedItems?.lineItems ?? false,
            isStatusEditable: status.lockedItems?.status ?? false,
            isCustomizable: status.customizable ?? false,
            nextStatusId: status.nextStatusId && status.nextStatusId !== '-' ? status.nextStatusId : null
        };
    };

    React.useEffect(() => {
        const { data, isLoading, error } = updateStatus;
        if (data && !isLoading) {
            showSuccess('Status configuration updated successfully');
        }
        if (error) {
            showError(typeof error === 'string' ? error : 'Failed to update status configuration');
        }
    }, [updateStatus, showSuccess, showError]);

    const BANNER_RATIO = 595 / 110;

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const rawFile = event.target.files?.[0];
        if (!rawFile) return;
        event.target.value = '';
        if (!rawFile.type.startsWith('image/')) {
            showError('Please select an image file');
            return;
        }

        const img = new Image();
        const objectUrl = URL.createObjectURL(rawFile);
        img.onload = () => {
            URL.revokeObjectURL(objectUrl);

            const srcAspect = img.width / img.height;
            let sx = 0, sy = 0, sw = img.width, sh = img.height;
            if (srcAspect > BANNER_RATIO) {
                sw = img.height * BANNER_RATIO;
                sx = (img.width - sw) / 2;
            } else {
                sh = img.width / BANNER_RATIO;
                sy = (img.height - sh) / 2;
            }

            const canvas = document.createElement('canvas');
            canvas.width = 1190;
            canvas.height = 220;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, sx, sy, sw, sh, 0, 0, 1190, 220);

            const mimeType = rawFile.type === 'image/png' ? 'image/png' : 'image/jpeg';
            const dataUrl = canvas.toDataURL(mimeType, 0.92);
            setUploadedImage(dataUrl);
            setUploadedFileName(rawFile.name);
            setInvoiceHeader(rawFile.name);

            canvas.toBlob(blob => {
                if (blob) setFile(new File([blob], rawFile.name, { type: mimeType }));
            }, mimeType, 0.92);
        };
        img.src = objectUrl;
    };

    const handleSave = async () => {
        if (!user?.entityId) {
            showError('Entity ID is missing');
            return;
        }

        try {
            const { url, method } = buildApiRequestConfig(routes.invoiceSettings.createOrUpdate, [
                { param: 'entityId', value: user.entityId }
            ]);

            const formData = new FormData();
            
            formData.append('provisionDateOffset', provisionDate);
            formData.append('termsAndConditions', termsAndConditions);
            formData.append('statusId', status);
            formData.append('nextInvoiceNumber', nextInvoiceNumber);
            formData.append('fromAddress', fromAddress);
            formData.append('fromPhone', fromPhone);
            formData.append('fromFax', fromFax);
            formData.append('fromEmail', fromEmail);
            formData.append('titlePosition', titlePosition);
            formData.append('hideHeaderImage', String(hideHeaderImage));
            formData.append('showTitle', String(showTitle));
            formData.append('showContactInfo', String(showContactInfo));

            // Add file if uploaded
            if (file) {
                formData.append('file', file);
                formData.append('headerFileName', uploadedFileName);
            }

            // Get token from cookies
            const token = document.cookie
                .split('; ')
                .find(row => row.startsWith('token='))
                ?.split('=')[1];

            const headers: HeadersInit = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            // Note: Don't set Content-Type for FormData - browser will set it with boundary

            const response = await fetch(url, {
                method,
                body: formData,
                headers
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update invoice settings');
            }

            showSuccess('Invoice settings updated successfully');
            
            // Reset changes tracking after successful save
            setOriginalValues({
                termsAndConditions,
                provisionDate,
                nextInvoiceNumber,
                invoiceHeader,
                status,
                fromAddress,
                fromPhone,
                fromFax,
                fromEmail,
                titlePosition,
                hideHeaderImage,
                showTitle,
                showContactInfo,
            });
            setHasChanges(false);
            setFile(null); // Clear file upload
            setUploadedFileName('');
            
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to update invoice settings';
            showError(errorMessage);
        }
    };


    return (
        <div style={{ width: '100%' }}>
            <Topbar title="Invoice Settings">
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                    <XeroSyncButton type="statuses" />
                </Box>
            </Topbar>
            <div style={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <SectionWrapper title="Status Configuration">
                    {!hasAdvanced && <PlanUpgradeBar onUpgrade={promptAdvanced} sx={{ mt: 2, mb: 1, maxWidth: 1200 }} />}
                    <Box maxWidth={1200} onPointerDownCapture={blockStatusEdit} onClickCapture={blockStatusEdit}>
                        <StatusConfigTable
                            statuses={statusConfigs}
                            statusOptions={statusOptions}
                            disabled={isReadOnly}
                            addDisabled={!canCreateStatus}
                            deleteDisabled={!canDeleteStatus}
                            onEdit={(status) => {
                                if (isReadOnly) return;
                                // Update local state
                                setStatusConfigs(prev => prev.map(s => s.id === status.id ? { ...s, name: status.name } : s));
                                // Also update statusOptions for the dropdown
                                setStatusOptions(prev => prev.map(opt => opt.value === String(status.id) ? { ...opt, label: status.name } : opt));
                                setCustomizableStatusOptions(prev => prev.map(opt => opt.value === String(status.id) ? { ...opt, label: status.name } : opt));
                                // Call API to update
                                const statusToUpdate = statusConfigs.find(s => s.id === status.id);
                                if (statusToUpdate) {
                                    const { url, method } = buildApiRequestConfig(routes.invoiceSettings.updateStatus, [{ param: 'id', value: status.id }]);
                                    setUpdateStatus({ 
                                        url, 
                                        method, 
                                        body: buildStatusUpdatePayload({ ...statusToUpdate, name: status.name })
                                    });
                                }
                            }}
                            onDelete={(statusId) => {
                                if (!canDeleteStatus) return;
                                const { url, method } = buildApiRequestConfig(routes.invoiceSettings.deleteStatus, [{ param: 'id', value: statusId }]);
                                setDeleteStatus({ url, method });
                            }}
                            onAddStatus={(config) => {
                                if (!canCreateStatus) return;
                                if (!config.name.trim()) {
                                    showError('Please enter a status name');
                                    return;
                                }
                                // Check if status already exists
                                if (statusOptions.some(opt => String(opt.value).toLowerCase() === config.name.trim().toLowerCase())) {
                                    showError('Status already exists');
                                    return;
                                }

                                // Add new status to local state immediately
                                const newStatus = {
                                    label: config.name.trim(),
                                    value: config.name.trim()
                                };
                                setStatusOptions(prev => [...prev, newStatus]);
                                showSuccess(`Status "${config.name.trim()}" added successfully`);

                                const { url, method } = buildApiRequestConfig(routes.invoiceSettings.createStatus);
                                setSaveStatus({ 
                                    url, 
                                    method, 
                                    body: { 
                                        entityId: user?.entityId,
                                        name: config.name.trim(),
                                        isDefault: config.isDefault,
                                        isActive: true,
                                        isCompanyEditable: config.isCompanyEditable,
                                        isLineItemEditable: config.isLineItemEditable,
                                        isStatusEditable: config.isStatusEditable,
                                        isCustomizable: config.isCustomizable,
                                        nextStatusId: config.nextStatusId || null
                                    } 
                                });
                            }}
                            onToggleDefault={(statusId) => {
                                // Update the statusConfigs to set this as default and unset others
                                setStatusConfigs(prev => prev.map(s => ({ ...s, isDefault: s.id === statusId })));
                                // Call API to update
                                const statusToUpdate = statusConfigs.find(s => s.id === statusId);
                                if (statusToUpdate) {
                                    const { url, method } = buildApiRequestConfig(routes.invoiceSettings.updateStatus, [{ param: 'id', value: statusId }]);
                                    setUpdateStatus({ 
                                        url, 
                                        method, 
                                        body: buildStatusUpdatePayload({ ...statusToUpdate, isDefault: true })
                                    });
                                }
                            }}
                            onToggleLocked={(statusId) => {
                                const statusToUpdate = statusConfigs.find(s => s.id === statusId);
                                const newLocked = statusToUpdate ? !statusToUpdate.isLocked : false;
                                setStatusConfigs(prev => prev.map(s => s.id === statusId ? { ...s, isLocked: newLocked } : s));
                                // Call API to update
                                if (statusToUpdate) {
                                    const { url, method } = buildApiRequestConfig(routes.invoiceSettings.updateStatus, [{ param: 'id', value: statusId }]);
                                    setUpdateStatus({ 
                                        url, 
                                        method, 
                                        body: buildStatusUpdatePayload(statusToUpdate)
                                    });
                                }
                            }}
                            onToggleLockedItem={(statusId, itemId) => {
                                const statusToUpdate = statusConfigs.find(s => s.id === statusId);
                                const newLockedItems = statusToUpdate 
                                    ? { ...statusToUpdate.lockedItems, [itemId]: !statusToUpdate.lockedItems?.[itemId as keyof typeof statusToUpdate.lockedItems] }
                                    : {};
                                setStatusConfigs(prev => prev.map(s => 
                                    s.id === statusId 
                                        ? {
                                            ...s,
                                            lockedItems: newLockedItems
                                        }
                                        : s
                                ));
                                // Call API to update
                                if (statusToUpdate) {
                                    const { url, method } = buildApiRequestConfig(routes.invoiceSettings.updateStatus, [{ param: 'id', value: statusId }]);
                                    setUpdateStatus({ 
                                        url, 
                                        method, 
                                        body: buildStatusUpdatePayload({ ...statusToUpdate, lockedItems: newLockedItems })
                                    });
                                }
                            }}
                            onToggleCustomizable={(statusId) => {
                                const statusToUpdate = statusConfigs.find(s => s.id === statusId);
                                const newCustomizable = statusToUpdate ? !statusToUpdate.customizable : false;
                                setStatusConfigs(prev => prev.map(s => s.id === statusId ? { ...s, customizable: newCustomizable } : s));
                                // Call API to update
                                if (statusToUpdate) {
                                    const { url, method } = buildApiRequestConfig(routes.invoiceSettings.updateStatus, [{ param: 'id', value: statusId }]);
                                    setUpdateStatus({ 
                                        url, 
                                        method, 
                                        body: buildStatusUpdatePayload({ ...statusToUpdate, customizable: newCustomizable })
                                    });
                                }
                            }}
                            onNextStatusChange={(statusId, nextStatus) => {
                                const statusToUpdate = statusConfigs.find(s => s.id === statusId);
                                setStatusConfigs(prev => prev.map(s => s.id === statusId ? { ...s, nextStatusId: nextStatus } : s));
                                // Call API to update
                                if (statusToUpdate) {
                                    const { url, method } = buildApiRequestConfig(routes.invoiceSettings.updateStatus, [{ param: 'id', value: statusId }]);
                                    setUpdateStatus({ 
                                        url, 
                                        method, 
                                        body: buildStatusUpdatePayload({ ...statusToUpdate, nextStatusId: nextStatus })
                                    });
                                }
                            }}
                        />
                    </Box>
                </SectionWrapper>
                <SectionWrapper title="Customization">
                    {/* Status selector */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <Typography sx={{ fontWeight: 600, minWidth: 60 }}>Status:</Typography>
                        <Box sx={{ flex: 1, minWidth: 0, maxWidth: 260 }}>
                            <Dropdown
                                value={String(status)}
                                onChange={(value: string) => setStatus(value)}
                                options={customizableStatusOptions}
                                placeholder="Select status..."
                                height={40}
                                width="100%"
                                disabled={isReadOnly}
                            />
                        </Box>
                    </Box>

                    {/* Two-panel layout - stacks on mobile since the live
                        preview panel is a fixed-aspect invoice mockup that
                        just gets crushed if squeezed side-by-side with the
                        fields on a phone-width screen. */}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'flex-start' }}>

                        {/* ── Left: fields ── */}
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>

                            {/* Logo upload */}
                            <Box>
                                <Typography sx={{ fontWeight: 600, mb: 1, fontSize: 13 }}>Logo / Header Image</Typography>
                                <Box sx={{
                                    border: '2px dashed #e0e0e0', borderRadius: 2,
                                    aspectRatio: '595 / 110', width: '100%',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: 'var(--color-container-background)', mb: 1, overflow: 'hidden',
                                    cursor: isReadOnly ? 'default' : 'pointer', opacity: isReadOnly ? 0.6 : 1,
                                    '&:hover': isReadOnly ? {} : { borderColor: 'var(--color-primary)' },
                                }}
                                    onClick={isReadOnly ? undefined : () => document.getElementById('image-upload')?.click()}
                                >
                                    {uploadedImage ? (
                                        <img src={uploadedImage} alt="Header" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <Typography sx={{ fontSize: 12, color: '#aaa' }}>Click to upload header image (5.4:1 ratio)</Typography>
                                    )}
                                </Box>
                                <input accept="image/*" style={{ display: 'none' }} id="image-upload" type="file" onChange={handleImageUpload} disabled={isReadOnly} />
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button size="small" variant="outlined" component="label" htmlFor="image-upload" disabled={isReadOnly}
                                        sx={{ fontSize: 12, textTransform: 'none', borderColor: '#e0e0e0', color: 'var(--color-text-muted)', opacity: isReadOnly ? 0.5 : 1 }}>
                                        {uploadedImage ? 'Change image' : 'Upload image'}
                                    </Button>
                                    {uploadedImage && (
                                        <Button size="small" disabled={isReadOnly} onClick={() => { setUploadedImage(null); setInvoiceHeader(''); setFile(null); }}
                                            sx={{ fontSize: 12, textTransform: 'none', color: isReadOnly ? 'var(--color-text-muted)' : '#e53935', opacity: isReadOnly ? 0.5 : 1 }}>
                                            Remove
                                        </Button>
                                    )}
                                </Box>
                                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)', mt: 0.75 }}>
                                    Images are automatically cropped to a 595px ✕ 110px aspect ratio (centre crop).
                                </Typography>
                            </Box>

                            {/* Header layout — compact */}
                            <Box>
                                <Typography sx={{ fontWeight: 600, mb: 1, fontSize: 13 }}>Header Layout</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                    <Box>
                                        <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)', mb: 0.5 }}>Title Position</Typography>
                                        <ToggleButtonGroup
                                            value={titlePosition}
                                            exclusive
                                            size="small"
                                            onChange={(_, val) => val && setTitlePosition(val)}
                                            disabled={isReadOnly}
                                            sx={{ height: 35 }}
                                        >
                                            <ToggleButton value="left" sx={{ textTransform: 'none', fontSize: 12, px: 1.25, gap: 0.5, '&.Mui-selected': { background: 'rgba(var(--color-primary-rgb), 0.1333)', color: 'var(--color-primary)' } }}>
                                                <AlignHorizontalLeftIcon fontSize="small" /> Left
                                            </ToggleButton>
                                            <ToggleButton value="right" sx={{ textTransform: 'none', fontSize: 12, px: 1.25, gap: 0.5, '&.Mui-selected': { background: 'rgba(var(--color-primary-rgb), 0.1333)', color: 'var(--color-primary)' } }}>
                                                <AlignHorizontalRightIcon fontSize="small" /> Right
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: 40 }}>
                                        <Toggle size="small" checked={showTitle} onChange={() => setShowTitle(prev => !prev)} disabled={isReadOnly} />
                                        <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Show Title</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: 40 }}>
                                        <Toggle size="small" checked={showContactInfo} onChange={() => setShowContactInfo(prev => !prev)} disabled={isReadOnly} />
                                        <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Show Contact Info</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: 40 }}>
                                        <Toggle size="small" checked={hideHeaderImage} onChange={() => setHideHeaderImage(prev => !prev)} disabled={isReadOnly} />
                                        <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Hide Header Image</Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Divider />

                            {/* Contact info */}
                            <Box>
                                <Typography sx={{ fontWeight: 600, mb: 1.5, fontSize: 13 }}>Contact Information</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box>
                                        <Typography sx={{ fontSize: 12, fontWeight: 500, mb: 0.5, color: 'var(--color-text-muted)' }}>Address</Typography>
                                        <TextField
                                            fullWidth size="small" value={fromAddress}
                                            onChange={(e) => setFromAddress(e.target.value)}
                                            multiline rows={2} placeholder="Enter address..."
                                            disabled={isReadOnly}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1, fontSize: 12 } }}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                        <Box sx={{ flex: 1, minWidth: 140 }}>
                                            <Typography sx={{ fontSize: 12, fontWeight: 500, mb: 0.5, color: 'var(--color-text-muted)' }}>Phone</Typography>
                                            <TextField
                                                fullWidth size="small" value={fromPhone}
                                                onChange={(e) => setFromPhone(e.target.value)}
                                                placeholder="e.g. +61 2 9000 0000"
                                                disabled={isReadOnly}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1, fontSize: 12 } }}
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 140 }}>
                                            <Typography sx={{ fontSize: 12, fontWeight: 500, mb: 0.5, color: 'var(--color-text-muted)' }}>Fax</Typography>
                                            <TextField
                                                fullWidth size="small" value={fromFax}
                                                onChange={(e) => setFromFax(e.target.value)}
                                                placeholder="e.g. +61 2 9000 0001"
                                                disabled={isReadOnly}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1, fontSize: 12 } }}
                                            />
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: 12, fontWeight: 500, mb: 0.5, color: 'var(--color-text-muted)' }}>Email</Typography>
                                        <TextField
                                            fullWidth size="small" value={fromEmail}
                                            onChange={(e) => setFromEmail(e.target.value)}
                                            placeholder="e.g. billing@company.com"
                                            disabled={isReadOnly}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1, fontSize: 12 } }}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            <Divider />

                            {/* Terms & Conditions */}
                            <Box>
                                <Typography sx={{ fontWeight: 600, mb: 1, fontSize: 13 }}>Terms & Conditions</Typography>
                                <TextField
                                    multiline rows={6} fullWidth
                                    value={termsAndConditions}
                                    onChange={(e) => setTermsAndConditions(e.target.value)}
                                    variant="outlined" size="small"
                                    placeholder="Enter terms and conditions..."
                                    disabled={isReadOnly}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1, fontSize: 12 } }}
                                />
                            </Box>

                            <Divider />

                            {/* Provision date */}
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: 13 }}>Default Provision Date</Typography>
                                    <Tooltip message="Used as the default provision date when creating an invoice, or when creating a new client." />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Invoice date +</Typography>
                                    <TextField
                                        value={provisionDate}
                                        onChange={(e) => setProvisionDate(e.target.value)}
                                        size="small" sx={{ width: 64, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        inputProps={{ style: { textAlign: 'center' } }}
                                        disabled={isReadOnly}
                                    />
                                    <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>days</Typography>
                                </Box>
                                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', fontStyle: 'italic', mt: 1 }}>
                                    This date will be used if a particular client has not made any payments. We recommend you make this 1.5 times your normal terms.
                                    Once a client's payment behavior is known (or a custom number of days is set for that client), this default is replaced by that calculated or set number of days.
                                </Typography>
                            </Box>

                            <Divider />

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button variant="contained" onClick={handleSave} disabled={!hasChanges || isReadOnly}
                                    sx={{ background: 'var(--color-primary)', color: '#fff', boxShadow: 'none', borderRadius: 2, textTransform: 'none', px: 4, '&:hover': { background: 'var(--color-primary-hover)' } }}>
                                    Save Changes
                                </Button>
                            </Box>
                        </Box>

                        {/* ── Right: live preview ── */}
                        <Box sx={{ flex: 0.5, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1.5, color: 'var(--color-text-muted)' }}>Preview</Typography>
                            <PreviewScaler>
                                <InvoicePreviewMock
                                    statusName={getStatusName(status) || 'Invoice'}
                                    logoUrl={uploadedImage}
                                    termsAndConditions={termsAndConditions}
                                    companyName={companySettings?.company?.name || ''}
                                    fromAddress={fromAddress}
                                    fromPhone={fromPhone}
                                    fromFax={fromFax}
                                    fromEmail={fromEmail}
                                    titlePosition={titlePosition}
                                    hideHeaderImage={hideHeaderImage}
                                    showTitle={showTitle}
                                    showContactInfo={showContactInfo}
                                />
                            </PreviewScaler>
                        </Box>
                    </Box>
                </SectionWrapper>
            </div>
        </div>
    );
};

export { InvoicePreviewMock, PreviewScaler } from '../components/invoice-preview-mock';

export default InvoiceSettings;